import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  PhoneAndroid as AndroidIcon,
  PhoneIphone as IosIcon,
  Computer as WebIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { adminApi } from '../services/api';
import { User, PaginatedResponse } from '../types';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

interface UserManagementProps {
  isVisible?: boolean;
}

const UserManagement: React.FC<UserManagementProps> = ({ isVisible = true }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        search: search || undefined,
        platform: platform === 'all' ? undefined : platform,
        sortBy,
        sortOrder,
      };

      const response: PaginatedResponse<User> = await adminApi.getUsers(params);
      setUsers(response.users);
      setTotalUsers(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchUsers, {
    refreshInterval: 90000, // 90 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search, platform, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handlePlatformChange = (event: any) => {
    setPlatform(event.target.value);
    setPage(0);
  };

  const handleSortChange = (newSortBy: string) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return <IosIcon color="primary" fontSize="small" />;
      case 'android':
        return <AndroidIcon color="success" fontSize="small" />;
      case 'web':
        return <WebIcon color="info" fontSize="small" />;
      default:
        return <WebIcon color="disabled" fontSize="small" />;
    }
  };

  const getPlatformColor = (platform: string): "primary" | "success" | "info" | "default" => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return 'primary';
      case 'android':
        return 'success';
      case 'web':
        return 'info';
      default:
        return 'default';
    }
  };

  const getVerificationStatus = (user: User) => {
    return user.isVerifyEmail ? (
      <Chip label="Doğrulandı" color="success" size="small" />
    ) : (
      <Chip label="Doğrulanmadı" color="warning" size="small" />
    );
  };

  if (error) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Alert 
          severity="error" 
          action={
            <IconButton color="inherit" size="small" onClick={fetchUsers}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          Kullanıcı Yönetimi
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Son güncelleme: {format(lastRefresh, 'HH:mm:ss')}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
          <Box 
            display="grid" 
            gridTemplateColumns={{ 
              xs: '1fr', 
              sm: '1fr', 
              md: '2fr 1fr 1fr auto',
              lg: '2fr 1fr 1fr auto'
            }} 
            gap={{ xs: 2, sm: 2.5, md: 3 }} 
            alignItems="center"
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Kullanıcı ara (isim veya email)..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platform}
                label="Platform"
                onChange={handlePlatformChange}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
                <MenuItem value="android">Android</MenuItem>
                <MenuItem value="web">Web</MenuItem>
                <MenuItem value="unknown">Bilinmeyen</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sıralama</InputLabel>
              <Select
                value={`${sortBy}-${sortOrder}`}
                label="Sıralama"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                  setPage(0);
                }}
              >
                <MenuItem value="createdAt-desc">En Yeni</MenuItem>
                <MenuItem value="createdAt-asc">En Eski</MenuItem>
                <MenuItem value="name-asc">İsim (A-Z)</MenuItem>
                <MenuItem value="name-desc">İsim (Z-A)</MenuItem>
                <MenuItem value="lastLoginAt-desc">Son Giriş</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Yenile">
              <IconButton onClick={manualRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb={{ xs: 2, sm: 2, md: 2 }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
          >
            <Typography variant="h6">
              Kullanıcılar ({totalUsers.toLocaleString()})
            </Typography>
            {loading && <CircularProgress size={24} />}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Konum</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSortChange('createdAt')}
                  >
                    Kayıt Tarihi
                    {sortBy === 'createdAt' && (
                      <span style={{ marginLeft: 4 }}>
                        {sortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSortChange('lastLoginAt')}
                  >
                    Son Giriş
                    {sortBy === 'lastLoginAt' && (
                      <span style={{ marginLeft: 4 }}>
                        {sortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                          {user.deviceInfo?.deviceName && (
                            <Typography variant="caption" color="text.secondary">
                              {user.deviceInfo.deviceName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Chip 
                          icon={getPlatformIcon(user.platform)}
                          label={user.platform || 'Bilinmeyen'}
                          color={getPlatformColor(user.platform)}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.city && user.country 
                          ? `${user.city}, ${user.country}`
                          : user.country || 'Belirtilmemiş'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getVerificationStatus(user)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(user.createdAt), 'dd.MM.yyyy HH:mm', { locale: tr })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLoginAt 
                          ? format(new Date(user.lastLoginAt), 'dd.MM.yyyy HH:mm', { locale: tr })
                          : 'Hiç giriş yapmadı'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}'dan fazla`}`
            }
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserManagement;
